import { isLivePath } from "@/lib/publication";

export const SITE = {
  name: "Casino Betalingen",
  domain: "casinobetalingen.com",
  url: "https://casinobetalingen.com",
  tagline: "Top 10 beste casino betaalmethoden getest",
  minAge: 24,
  address: {
    street: "Herengracht 450",
    postalCode: "1017 CA",
    city: "Amsterdam",
    country: "Nederland",
  },
  disclaimer:
    "CasinoBetalingen.com is een onafhankelijke vergelijkingswebsite. Sommige links zijn advertentielinks. Deelname aan kansspelen brengt risico's met zich mee. Speel alleen bij aanbieders die in Nederland zijn toegestaan. 18+.",
} as const;

/**
 * Redactionele placeholders. Zolang een waarde nog tussen accolades staat is
 * hij niet ingevuld: publiceer die gegevens dan niet en toon geen schema.
 */
export const LEGAL_PLACEHOLDERS = {
  LEGAL_BV_NAME: "{{LEGAL_BV_NAME}}",
  TRADE_NAME: "{{TRADE_NAME}}",
  KVK_NUMBER: "{{KVK_NUMBER}}",
  VAT_NUMBER: "{{VAT_NUMBER}}",
  STREET: "{{STREET}}",
  HOUSE_NUMBER: "{{HOUSE_NUMBER}}",
  POSTAL_CODE: "{{POSTAL_CODE}}",
  CITY: "{{CITY}}",
  COUNTRY: "{{COUNTRY}}",
  CONTACT_EMAIL: "{{CONTACT_EMAIL}}",
  PHONE: "{{PHONE}}",
  EDITOR_NAME: "{{EDITOR_NAME}}",
  EDITOR_LINKEDIN: "{{EDITOR_LINKEDIN}}",
} as const;

export function isPlaceholder(value: string | null | undefined): boolean {
  return !value || (value.startsWith("{{") && value.endsWith("}}"));
}

/** Geeft de waarde terug, of null wanneer het nog een oningevulde placeholder is. */
export function realValue(value: string | null | undefined): string | null {
  return isPlaceholder(value) ? null : (value as string);
}

export type NavLink = { label: string; href: string; draft?: boolean };

const MEGA_MENU_ALL: { title: string; links: NavLink[] }[] = [
  {
    title: "Populair",
    links: [
      { label: "Alle betaalmethoden", href: "/betaalmethoden" },
      { label: "iDEAL casino's", href: "/betaalmethoden/ideal" },
      { label: "Trustly casino's", href: "/betaalmethoden/trustly" },
      { label: "PayPal casino's", href: "/betaalmethoden/paypal" },
      { label: "Paysafecard casino's", href: "/betaalmethoden/paysafecard" },
      { label: "Klarna casino's", href: "/betaalmethoden/klarna" },
      { label: "AfterPay casino's", href: "/betaalmethoden/afterpay-casinos" },
    ],
  },
  {
    title: "E-wallets en kaarten",
    links: [
      { label: "Skrill casino's", href: "/betaalmethoden/skrill" },
      { label: "Neteller casino's", href: "/betaalmethoden/neteller" },
      { label: "Revolut casino's", href: "/betaalmethoden/revolut" },
      { label: "MuchBetter casino's", href: "/betaalmethoden/muchbetter" },
      { label: "Visa casino's", href: "/betaalmethoden/visa" },
      { label: "Mastercard casino's", href: "/betaalmethoden/mastercard" },
      { label: "Creditcard casino's", href: "/betaalmethoden/creditcard" },
      { label: "Apple Pay casino's", href: "/betaalmethoden/apple-pay" },
      { label: "Google Pay casino's", href: "/betaalmethoden/google-pay" },
    ],
  },
  {
    title: "Overig en lage storting",
    links: [
      { label: "Neosurf casino's", href: "/betaalmethoden/neosurf" },
      { label: "Gokken via telefoonrekening", href: "/betaalmethoden/telefoonrekening" },
      { label: "Sofort casino's", href: "/betaalmethoden/sofort" },
      { label: "HiPay casino's", href: "/betaalmethoden/hipay" },
      { label: "Bankoverschrijving casino's", href: "/betaalmethoden/bankoverschrijving", draft: true },
      { label: "1 euro deposit casino", href: "/lage-storting/1-euro-deposit-casinos" },
      { label: "5 euro deposit casino", href: "/lage-storting/5-euro-deposit-casinos" },
      { label: "10 euro deposit casino", href: "/lage-storting/10-euro-deposit-casinos" },
      { label: "20 euro deposit casino", href: "/lage-storting/20-euro-deposit-casinos" },
    ],
  },
  {
    title: "Gidsen",
    links: [
      { label: "Nieuwe online casino's", href: "/nieuwe-online-casinos" },
      { label: "Betrouwbare online casino's", href: "/betrouwbare-online-casinos" },
      { label: "iDIN casino's", href: "/idin-casinos" },
      { label: "Casino zonder iDIN", href: "/online-casino-zonder-idin" },
      { label: "Casino zonder BKR", href: "/online-casino-zonder-bkr" },
      { label: "Online casino buitenland", href: "/online-casino-buitenland" },
      { label: "Casino's zonder transactiekosten", href: "/casinozoeker?kosten=geen" },
    ],
  },
];

/** Prominent eigen item in de hoofdnavigatie. */
export const CRUKS_NAV: NavLink = { label: "Casino zonder Cruks", href: "/casino-zonder-cruks" };

const PRIMARY_NAV_ALL: NavLink[] = [
  { label: "Casinozoeker", href: "/casinozoeker" },
  { label: "Nieuws", href: "/nieuws" },
];

/** Dropdown in de hoofdnavigatie met de uitbetaal-gidsen. */
const PAYOUT_NAV_ALL: NavLink[] = [
  { label: "Snel uitbetalende casino's", href: "/online-casino-snelle-uitbetaling" },
  { label: "Best uitbetalende casino's", href: "/best-uitbetalende-online-casinos" },
  { label: "Casino zonder limiet", href: "/online-casino-zonder-limiet" },
  { label: "AX.bet uitbetaling", href: "/review/axbet-uitbetaling" },
  { label: "Wakuna uitbetaling", href: "/review/wakuna-uitbetaling" },
  { label: "bet365 uitbetaling", href: "/review/bet365-uitbetaling" },
  { label: "One Casino uitbetaling", href: "/review/one-casino-uitbetaling" },
  { label: "TonyBet uitbetaling", href: "/review/tonybet-uitbetaling" },
  { label: "Kokobet uitbetaling", href: "/review/kokobet-uitbetaling" },
  { label: "TOTO uitbetaling", href: "/review/toto-uitbetaling" },
  { label: "Jack's uitbetaling", href: "/review/jacks-uitbetaling" },
  { label: "Spinboss uitbetaling", href: "/review/spinboss-uitbetaling" },
];

/**
 * Footer: vier compacte kolommen + een inline rij met alle betaalmethoden.
 * Samen bevatten ze elke live pagina uit het menu, zodat crawlers (die de
 * dichtgeklapte dropdowns niet zien) alle interne links blijven vinden.
 */
const FOOTER_NAV_ALL: { title: string; links: NavLink[] }[] = [
  {
    title: "Vergelijken",
    links: [
      { label: "Alle betaalmethoden", href: "/betaalmethoden" },
      { label: "Casinozoeker", href: "/casinozoeker" },
      { label: "Lage stortingen", href: "/lage-storting" },
      { label: "1 euro deposit casino", href: "/lage-storting/1-euro-deposit-casinos" },
      { label: "5 euro deposit casino", href: "/lage-storting/5-euro-deposit-casinos" },
      { label: "10 euro deposit casino", href: "/lage-storting/10-euro-deposit-casinos" },
      { label: "20 euro deposit casino", href: "/lage-storting/20-euro-deposit-casinos" },
      { label: "Betaalnieuws", href: "/nieuws" },
    ],
  },
  {
    title: "Gidsen",
    links: [
      { label: "Casino zonder Cruks", href: "/casino-zonder-cruks" },
      { label: "Nieuwe online casino's", href: "/nieuwe-online-casinos" },
      { label: "Betrouwbare online casino's", href: "/betrouwbare-online-casinos" },
      { label: "iDIN casino's", href: "/idin-casinos" },
      { label: "Casino zonder iDIN", href: "/online-casino-zonder-idin" },
      { label: "Casino zonder BKR", href: "/online-casino-zonder-bkr" },
      { label: "Online casino buitenland", href: "/online-casino-buitenland" },
    ],
  },
  {
    title: "Uitbetalen",
    links: [{ label: "Uitbetalen", href: "/uitbetalen" }, ...PAYOUT_NAV_ALL],
  },
  {
    title: "Over ons",
    links: [
      { label: "Over ons", href: "/over-ons" },
      { label: "Redactie & auteurs", href: "/auteurs" },
      { label: "Methodologie", href: "/methodologie" },
      { label: "Redactioneel beleid", href: "/redactioneel-beleid" },
      { label: "Kennisbank", href: "/kennisbank" },
      { label: "Contact", href: "/contact" },
      { label: "Verantwoord spelen", href: "/verantwoord-spelen" },
      { label: "Affiliate disclosure", href: "/affiliate-disclosure" },
      { label: "Privacybeleid & cookies", href: "/privacybeleid" },
      { label: "Algemene voorwaarden", href: "/algemene-voorwaarden" },
    ],
  },
];

/** Inline rij in de footer met alle betaalmethodepagina's. */
const FOOTER_METHODS_ALL: NavLink[] = MEGA_MENU_ALL.flatMap((g) => g.links).filter((l) =>
  l.href.startsWith("/betaalmethoden/"),
);

/**
 * Navigatie bevat uitsluitend live pagina's. Conceptpagina's worden hier
 * (en daarmee overal in de interne linkstructuur) weggefilterd.
 */
const liveLinks = (links: NavLink[]) => links.filter((l) => isLivePath(l.href));
const liveGroups = (groups: { title: string; links: NavLink[] }[]) =>
  groups
    .map((g) => ({ ...g, links: liveLinks(g.links) }))
    .filter((g) => g.links.length > 0);

export const MEGA_MENU = liveGroups(MEGA_MENU_ALL);
export const PRIMARY_NAV = liveLinks(PRIMARY_NAV_ALL);
export const PAYOUT_NAV = liveLinks(PAYOUT_NAV_ALL);
export const FOOTER_NAV = FOOTER_NAV_ALL.map((g) => ({
  ...g,
  links: liveLinks(g.links),
})).filter((g) => g.links.length > 0);
export const FOOTER_METHODS = liveLinks(FOOTER_METHODS_ALL);
